/**
 * Global Settings — tenant-wide defaults (currency, country, timezone, date
 * & number formatting, fiscal year start month, default UoM system, etc.).
 * See api/app/Http/Controllers/GlobalSettingsController.php for the
 * resolution order (company row → platform-wide fallback → hardcoded
 * defaults) and web/src/modules/core/pages/GlobalSettingsPage.tsx for the
 * admin form that edits this.
 *
 * This module also keeps a couple of tiny module-level caches warm so
 * non-hook call sites (formatCurrency in shared/ui/utils.ts) and components
 * that only need a *default* (CountrySelect's new-record India default) can
 * read the resolved value without threading a query through every call
 * site. `useGlobalSettingsCacheSync()` — mounted once in AppShell — is what
 * keeps them current.
 */
import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { setActiveDateFormat, type DateFormatToken } from '@/shared/i18n/date-format';

export type LetterheadStyle = 'classic' | 'modern' | 'minimal';
export type LetterheadLogoPosition = 'left' | 'center' | 'right';
export type PrintPaperSize = 'A4' | 'Letter';
export type PrintStripeStyle = 'solid' | 'gradient';
export type PrintSignatureSide = 'left' | 'right' | 'both';

export interface GlobalSettingsData {
  id: number | null;
  company_id: number | null;
  default_currency: string;
  currency_symbol: string | null;
  default_country_id: number | null;
  default_state_id: number | null;
  default_timezone: string;
  date_format: DateFormatToken;
  time_format: '12h' | '24h';
  number_format: 'en-IN' | 'en-US';
  fiscal_year_start_month: number;
  default_uom_system: string;
  default_payment_term_id: number | null;
  default_tax_id: number | null;
  low_stock_threshold_days: number;
  session_timeout_minutes: number;
  /* Print & letterhead — drives the shared <PrintableDoc> layout. */
  letterhead_style: LetterheadStyle;
  letterhead_accent_color: string;
  letterhead_show_logo: boolean;
  letterhead_logo_position: LetterheadLogoPosition;
  print_footer_terms: string | null;
  print_signature_left_label: string | null;
  print_signature_right_label: string | null;
  print_show_page_numbers: boolean;
  print_paper_size: PrintPaperSize;
  /* Modern letterhead — Y8 additions. */
  print_stripe_style: PrintStripeStyle;
  print_show_watermark: boolean;
  print_signature_side: PrintSignatureSide;
  print_thankyou_note?: string | null;
  resolved: true;
}

interface Envelope<T> {
  data: T;
}

export const GLOBAL_SETTINGS_QUERY_KEY = ['settings', 'global'] as const;

/** Fetches the resolved global settings row. */
export function useGlobalSettings() {
  return useQuery({
    queryKey: GLOBAL_SETTINGS_QUERY_KEY,
    queryFn: async (): Promise<GlobalSettingsData> => {
      const { data } = await api.get<Envelope<GlobalSettingsData>>('/api/v1/settings/global');
      return data.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/* -------------------------------------------------------------------------- */
/*  Module-level caches for non-hook / default-value call sites               */
/* -------------------------------------------------------------------------- */

let cachedDefaultCurrency = 'INR';
/** Non-hook accessor — used by `formatCurrency()` when no currency is passed. */
export function getCachedDefaultCurrency(): string {
  return cachedDefaultCurrency;
}

let cachedCurrencySymbol: string | null = null;
/**
 * Non-hook accessor — the tenant-configured display symbol (₹, $, €, …).
 * When null, `formatCurrency()` falls back to the ISO-derived Intl symbol
 * for the resolved `default_currency`.
 */
export function getCachedCurrencySymbol(): string | null {
  return cachedCurrencySymbol;
}

let cachedDefaultCountry: { iso2: string; name: string; dialCode: string | null } = {
  iso2: 'IN',
  name: 'India',
  dialCode: '+91',
};
/** Non-hook accessor — used by `CountrySelect`'s new-record default. */
export function getCachedDefaultCountry(): { iso2: string; name: string; dialCode: string | null } {
  return cachedDefaultCountry;
}

/**
 * The tenant's dialling prefix, normalised to a leading `+`.
 *
 * Phone inputs show this as a fixed prefix so an operator types only the
 * local part — retyping "+91" on every number is the kind of small friction
 * that gets noticed a hundred times a day.
 */
export function getCachedDialCode(): string | null {
  const raw = cachedDefaultCountry.dialCode;
  if (!raw) return null;
  const trimmed = raw.trim();
  return trimmed.startsWith('+') ? trimmed : `+${trimmed}`;
}

interface CountryLookupRow {
  id: number;
  iso2: string;
  name: string;
  dial_code?: string | null;
}

/**
 * Mount once near the app root (AppShell) so the caches above stay warm for
 * the whole authenticated session — every page benefits without adding its
 * own query. Reuses the ['countries'] query key so it shares cache with
 * `useCountries()` in country-state-select.tsx rather than double-fetching.
 */
export function useGlobalSettingsCacheSync(): void {
  const { data: settings } = useGlobalSettings();
  const { data: countries } = useQuery({
    queryKey: ['countries'],
    queryFn: async (): Promise<CountryLookupRow[]> => {
      const { data } = await api.get<Envelope<CountryLookupRow[]>>('/api/v1/countries');
      return data.data ?? [];
    },
    staleTime: Infinity,
    enabled: !!settings?.default_country_id,
  });

  useEffect(() => {
    if (settings?.default_currency) {
      cachedDefaultCurrency = settings.default_currency;
    }
    // Empty string is treated as "not set" so it falls back to Intl.
    const sym = settings?.currency_symbol;
    cachedCurrencySymbol = sym && sym.trim() !== '' ? sym : null;
    // Push the tenant's chosen date-format token into the i18n module so
    // every formatDate()/formatDateTime() call site renders in that shape
    // without needing to know about the setting. See
    // shared/i18n/date-format.ts.
    setActiveDateFormat(settings?.date_format ?? null);
  }, [settings]);

  useEffect(() => {
    if (!settings?.default_country_id || !countries) return;
    const match = countries.find((c) => c.id === settings.default_country_id);
    if (match) {
      cachedDefaultCountry = {
        iso2: match.iso2,
        name: match.name,
        // Keep the seeded fallback when the country row has no dial code
        // rather than blanking a working prefix.
        dialCode: match.dial_code ?? cachedDefaultCountry.dialCode,
      };
    }
  }, [settings, countries]);
}
